import css from "./LibraryPage.sass?inline"
import {createElement, PageContext, PageFactory, RouteLocation} from "@opendaw/lib-jsx"
import {StudioService} from "@/service/StudioService"
import {Html} from "@opendaw/lib-dom"
import {getAllTracks, onTracksChange, formatDuration, deleteTrack, StoredTrack} from "@/ui/pages/TrackStore"
import {addFlashlightEffect} from "@/ui/pages/FlashlightEffect"
import {openPlayer} from "@/ui/pages/MusicPlayerPanel"
import {downloadTrackAudio} from "@/ui/pages/AudioDownload"

const className = Html.adoptStyleSheet(css, "LibraryPage")

const TABS = ["Songs", "Playlists", "Workspaces", "Studio Projects", "Cover Art", "Hooks", "Liked Hooks", "History"]

export const LibraryPage: PageFactory<StudioService> = ({}: PageContext<StudioService>) => {
    let searchQuery = ""
    let sortOrder: "newest" | "oldest" = "newest"

    // Tab bar
    const tabBar: HTMLDivElement = <div className="tab-bar"/> as unknown as HTMLDivElement
    const tabButtons: HTMLButtonElement[] = []

    for (let i = 0; i < TABS.length; i++) {
        const btn: HTMLButtonElement = (
            <button className={i === 0 ? "active" : ""}>{TABS[i]}</button>
        ) as unknown as HTMLButtonElement
        btn.onclick = () => {
            tabButtons.forEach((b, j) => b.classList.toggle("active", j === i))
        }
        tabButtons.push(btn)
        tabBar.appendChild(btn)
    }

    // Search
    const searchInput: HTMLInputElement = (
        <input className="search-input" type="text" placeholder="Search"/>
    ) as unknown as HTMLInputElement
    searchInput.oninput = () => {
        searchQuery = searchInput.value.toLowerCase()
        renderSongs()
    }

    // Sort button
    const sortBtn: HTMLButtonElement = (
        <button className="sort-btn">{"\u2261"} Newest</button>
    ) as unknown as HTMLButtonElement
    sortBtn.onclick = () => {
        sortOrder = sortOrder === "newest" ? "oldest" : "newest"
        sortBtn.textContent = `\u2261 ${sortOrder === "newest" ? "Newest" : "Oldest"}`
        renderSongs()
    }

    // Song list
    const songList: HTMLDivElement = <div className="song-list"/> as unknown as HTMLDivElement

    function renderSongs(): void {
        songList.innerHTML = ""
        let tracks = getAllTracks()

        // Filter by search
        if (searchQuery) {
            tracks = tracks.filter(t =>
                t.title.toLowerCase().includes(searchQuery) ||
                t.description.toLowerCase().includes(searchQuery)
            )
        }

        // Sort
        tracks.sort((a, b) => sortOrder === "newest" ? b.createdAt - a.createdAt : a.createdAt - b.createdAt)

        if (tracks.length === 0) {
            const empty: HTMLDivElement = (
                <div className="empty-state">
                    <div className="empty-icon">{"\u266B"}</div>
                    <div className="empty-text">No songs yet</div>
                    <div className="empty-sub">Create your first track to see it here</div>
                </div>
            ) as unknown as HTMLDivElement
            songList.appendChild(empty)
            return
        }

        for (const track of tracks) {
            songList.appendChild(track.loading ? makeLoadingItem(track) : makeTrackItem(track))
        }
    }

    function makeLoadingItem(track: StoredTrack): HTMLDivElement {
        return (
            <div className="song-item loading">
                <div className="song-art loading-art"/>
                <div className="song-info">
                    <div className="song-title">
                        {track.title}
                        <span className="generating-badge">Generating</span>
                    </div>
                    <div className="song-desc">{track.description}</div>
                    <div className="song-meta loading-meta"/>
                </div>
            </div>
        ) as unknown as HTMLDivElement
    }

    function makeTrackItem(track: StoredTrack): HTMLDivElement {
        const timeAgo = getTimeAgo(track.createdAt)
        const item: HTMLDivElement = (
            <div className="song-item">
                <div className="song-art">
                    <span className="song-icon">{"\u266B"}</span>
                    <span className="play-overlay">{"▶"}</span>
                    <span className="song-duration">{formatDuration(track.duration)}</span>
                </div>
                <div className="song-info">
                    <div className="song-title">
                        {track.title}
                        {track.shared ? <span className="shared-badge">Shared</span> : null}
                    </div>
                    <div className="song-desc">{track.description}</div>
                    <div className="song-meta">{track.model} &middot; {track.bpm} BPM &middot; {timeAgo}</div>
                </div>
                <div className="song-actions">
                    <button className="action-btn" title="Open in Studio">{"\u270E"}</button>
                    <div className="dl-wrap">
                        <button className="action-btn dl-btn" title="Download">{"\u2193"}</button>
                        <div className="dl-menu">
                            <button className="dl-opt">MP3</button>
                            <button className="dl-opt">WAV</button>
                        </div>
                    </div>
                    <button className="action-btn delete-btn" title="Delete">{"\u2715"}</button>
                </div>
            </div>
        ) as unknown as HTMLDivElement
        item.onclick = () => openPlayer(track)
        const openBtn = item.querySelector(".action-btn:first-child") as HTMLButtonElement
        openBtn.onclick = (event) => { event.stopPropagation(); RouteLocation.get().navigateTo("/studio") }
        const dlWrap = item.querySelector(".dl-wrap") as HTMLDivElement
        const dlBtn = dlWrap.querySelector(".dl-btn") as HTMLButtonElement
        const dlMenu = dlWrap.querySelector(".dl-menu") as HTMLDivElement
        dlBtn.onclick = (event) => { event.stopPropagation(); dlMenu.classList.toggle("open") }
        document.addEventListener("click", () => dlMenu.classList.remove("open"), {capture: false})
        const [mp3Opt, wavOpt] = dlMenu.querySelectorAll(".dl-opt") as unknown as HTMLButtonElement[]
        mp3Opt.onclick = (event) => {
            event.stopPropagation()
            dlMenu.classList.remove("open")
            if (track.audioUrl) downloadTrackAudio(track.audioUrl, track.title, "mp3").catch(console.error)
        }
        wavOpt.onclick = (event) => {
            event.stopPropagation()
            dlMenu.classList.remove("open")
            if (track.audioUrl) downloadTrackAudio(track.audioUrl, track.title, "wav").catch(console.error)
        }
        const delBtn = item.querySelector(".delete-btn") as HTMLButtonElement
        delBtn.onclick = (event) => { event.stopPropagation(); deleteTrack(track.id) }
        return item
    }

    renderSongs()
    onTracksChange(renderSongs)

    const root: HTMLDivElement = (
        <div className={className}>
            <div className="library-content">
                <div className="library-header">
                    <h1>Library</h1>
                </div>
                {tabBar}
                <div className="filter-bar">
                    <div className="search-wrapper">
                        <span className="search-icon">{"\u{1F50D}"}</span>
                        {searchInput}
                    </div>
                    {sortBtn}
                </div>
                {songList}
            </div>
        </div>
    ) as unknown as HTMLDivElement
    addFlashlightEffect(root)
    return root
}

function getTimeAgo(ts: number): string {
    const diff = Date.now() - ts
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return "Just now"
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return new Date(ts).toLocaleDateString()
}
